// PropertyManagementAgreementPage — /send/property-management-agreement route:
// the UNIFIED builder for the Exclusive Property Management Agreement (EPMA).
//
// ONE builder, ONE flow: staff fill what they already know AND an owner intake
// ALWAYS goes out. Every submission creates an owner-intake (POST
// /api/owner-intake/initiate); each owner is emailed a short form to answer/confirm
// what staff deferred, the first owner to finish locks it in, and the agreement is
// generated and sent for signature automatically. There is no separate "fill it
// yourself" path.
//
// For each intake-able fact staff use a per-field control:
//   • Yes / No            → staff answer is final; the owner is NOT asked.
//   • Ask the owner       → the owner answers it on their form.
//   • + "Confirm with owner" (on a Yes/No) → the owner is shown the staff answer to
//     confirm or correct.
// The §25 notice block (name / address / email for legal notices) is ALWAYS an
// owner question — the owner enters it on the form, never staff.
//
// The feature is DORMANT by default (a legal document). When EPMA_SEND_ENABLED is
// off the initiate route answers 503 and we show a friendly "not enabled yet" state.
//
// MODIFY MODE (?from=<groupId>): re-opens the builder pre-loaded from an existing
// engagement (GET /api/owner-intake/:groupId/detail) — property, deal terms,
// per-field controls and owners are seeded. Sending creates a NEW engagement and
// best-effort soft-cancels the previous one so the owners never hold two live intake
// links.
//
// TWO entry points:
//   • Intake phase (no &mode): the legacy Modify from the OwnerIntakeSection. If an
//     owner already provided answers (`intakeGathered`), staff first choose: carry
//     those answers in as final values (DEFAULT) or redo the intake and ask
//     everything again.
//   • Out-for-signature (&mode=<...>): the state-aware Modify from a live EPMA
//     submission (ThreadDetailPage → Modify). The current submission is already
//     withdrawn (cancel-then-rebuild) before we get here, and the mode dictates the
//     seed + the re-send email — the redo-vs-use-existing choice is BYPASSED:
//       - 'update'          — 0-signed simple update: carry existing answers in as
//                             final, and the re-send emails the bespoke "your
//                             agreement has been updated" notice (mode threaded to
//                             the initiate → epmaUpdated).
//       - 'restart-signing' — partial-signed restart: carry existing answers in,
//                             normal invite copy.
//       - 'restart-intake'  — partial-signed restart: redo the intake (re-ask
//                             everything), normal invite copy.
//
// EXISTING-OWNER DETECTION (owner-sync spec §3): when an owner's email is left
// (blur) we ask GET /api/owners/lookup. One match → a banner on that owner card
// (Use this owner / No, new owner); several → a picker + "New owner". Linking
// sends owners[i].appfolioOwnerId and shows "Collect a new owner intake anyway"
// (collectIntakeAnyway). No matches — including a switched-off lookup — changes
// nothing, and the request body is then exactly today's. The AppFolio owner id
// is a request field only; it is never rendered.
//
// EXISTING vs NEW OWNER (spec §13.1): when owner matching is on (one probe on
// load — lib/ownerSyncClient.probeOwnerMatch), each owner card is explicitly an
// Existing owner (picked from AppFolio with a search picker; the email lookup
// still assists) or a New owner (typed; after the name is typed,
// GET /api/owners/similar warns about a likely existing owner). When every
// owner is existing, "Collect owner details anyway" sends the contact-only
// intake. With matching off the choice never appears and the form is today's.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from '@/components/Toast';
import { OwnerMatchPrompt } from '@/components/ownerSync/OwnerMatchPrompt';
import { OwnerSearchPicker } from '@/components/ownerSync/OwnerSearchPicker';
import { SimilarOwnerWarning } from '@/components/ownerSync/SimilarOwnerWarning';
import { ApiError } from '@/lib/api';
import {
  allOwnersExisting,
  applyOwnerMatch,
  chainPlanSummary,
  CONTACT_ONLY_OVERRIDE_HELPER,
  CONTACT_ONLY_OVERRIDE_LABEL,
  normalizeEmail,
  ownerMatchPromptFor,
  similarCandidatesFor,
  similarNameKey,
  similarQueryFor,
  type EpmaOwnerKind,
  type SimilarOwner,
  type OwnerMatch,
  ownerMatchInitiateError,
} from '@/lib/ownerSync';
import { findSimilarOwners, lookupOwnersByEmail, probeOwnerMatch } from '@/lib/ownerSyncClient';
import {
  isEmailValid,
  type EpmaOwnerType,
  type EpmaPropertyType,
  type EpmaUtilityChoice,
} from '@/lib/epmaClient';
import {
  cancelOwnerIntake,
  epmaNewOwnerFields,
  getOwnerIntakeDetail,
  initiateOwnerIntake,
  regenerateOwnerAgreement,
  OwnerIntakeNotEnabledError,
  type OwnerIntakeAnswers,
  type OwnerIntakeDetail,
  type OwnerIntakeFieldControl,
  type OwnerIntakeFieldValue,
  type OwnerIntakeSendMode,
  type InitiateOwnerIntakeResult,
} from '@/lib/ownerIntakeClient';

// A staff-only change (date/rent/utilities/…) on a not-yet-fully-signed agreement
// REGENERATES + re-sends for signature — NO intake round-trip. An intake is sent
// ONLY when the modified config requires NEW owner-collected information (see
// epmaNewOwnerFields). Human labels for the owner-facing fields, for the copy.
const OWNER_FIELD_LABELS: Record<string, string> = {
  whpPaysPropertyTaxes: 'property taxes',
  whpPaysInsurance: 'insurance',
  whpPaysMortgage: 'the mortgage',
  hasIrrigation: 'irrigation',
  needsWinterizing: 'winterization',
  inHoa: 'the HOA',
  wantsWhpPayHoaDues: 'HOA dues',
};

// ---------------------------------------------------------------------------
// Owners
// ---------------------------------------------------------------------------

interface OwnerRow {
  key: number;
  ownerType: EpmaOwnerType;
  firstName: string;
  lastName: string;
  entityName: string;
  signerTitle: string;
  email: string;
  /** §13.1: staff's explicit choice. Only offered while owner matching is on; default new. */
  ownerKind: EpmaOwnerKind;
  /** §13.1: the last similar-name check, keyed by the name it ran for (similarNameKey). */
  similar?: { key: string; candidates: SimilarOwner[] } | undefined;
  /** §13.1: the name key staff answered "It's a different owner" for. */
  similarDismissedKey?: string | undefined;
  /** §13.1: the kind the row had before a link — "Not this owner" goes back to it. */
  ownerKindBeforeLink?: EpmaOwnerKind | undefined;
  /** The existing AppFolio owner staff linked this row to (§3). Sent on initiate; NEVER rendered. */
  appfolioOwnerId?: string | undefined;
  /** The match that was linked — its name / city / W-9 state drive the chip + override helper. */
  linkedMatch?: OwnerMatch | undefined;
  /** The last owner lookup, keyed by the normalized email it ran for. */
  lookup?: { email: string; matches: OwnerMatch[] } | undefined;
  /** The normalized email staff answered "No, new owner" for (the prompt stays away for it). */
  dismissedEmail?: string | undefined;
}

/** The name a row goes by in staff copy (entity name for an entity). */
function ownerRowName(o: OwnerRow): string {
  if (o.ownerType === 'entity' && o.entityName.trim()) return o.entityName.trim();
  return `${o.firstName} ${o.lastName}`.trim();
}

let nextKey = 1;
const blankOwner = (): OwnerRow => ({
  key: nextKey++,
  ownerType: 'individual',
  firstName: '',
  lastName: '',
  entityName: '',
  signerTitle: '',
  email: '',
  ownerKind: 'new',
});

const UTILITY_KEYS: Array<{ key: keyof UtilityState; label: string }> = [
  { key: 'water', label: 'Water' },
  { key: 'sewer', label: 'Sewer' },
  { key: 'garbage', label: 'Garbage' },
  { key: 'electricity', label: 'Electricity' },
  { key: 'gas', label: 'Gas' },
  { key: 'yardCare', label: 'Yard care' },
];

interface UtilityState {
  water: EpmaUtilityChoice;
  sewer: EpmaUtilityChoice;
  garbage: EpmaUtilityChoice;
  electricity: EpmaUtilityChoice;
  gas: EpmaUtilityChoice;
  yardCare: EpmaUtilityChoice;
}

/** Pre-compute endDate (start + 1 year − 1 day) for the live preview. */
function computeEndDate(startDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return '';
  const parts = startDate.split('-').map(Number);
  const [y, m, d] = parts as [number, number, number];
  const base = new Date(Date.UTC(y + 1, m - 1, d));
  base.setUTCDate(base.getUTCDate() - 1);
  return base.toISOString().slice(0, 10);
}

function formatLong(ymd: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
  const d = new Date(ymd + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Per-field control (Yes / No / Ask the owner + a "Confirm with owner" checkbox)
// ---------------------------------------------------------------------------

const FIELD_OPTIONS: Array<{ value: OwnerIntakeFieldValue; label: string }> = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'ask_owner', label: 'Ask the owner' },
];

const blankControl = (value: OwnerIntakeFieldValue = 'ask_owner'): OwnerIntakeFieldControl => ({
  value,
  confirmWithOwner: false,
});

/**
 * The unified per-field control. Radio Yes / No / Ask the owner, plus (for a
 * Yes/No) a "Confirm with the owner" checkbox that has the owner confirm/correct
 * the staff answer instead of it being final.
 */
function FieldControlField(props: {
  id: string;
  label: string;
  hint?: string;
  value: OwnerIntakeFieldControl;
  onChange: (v: OwnerIntakeFieldControl) => void;
}): React.JSX.Element {
  const { value } = props;
  const isYesNo = value.value === 'yes' || value.value === 'no';
  return (
    <div className="field" style={{ margin: 0 }}>
      <label htmlFor={props.id} style={{ marginBottom: 'var(--space-2)' }}>
        {props.label}
      </label>
      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        {FIELD_OPTIONS.map((o) => (
          <label
            key={o.value}
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', margin: 0, fontWeight: 'normal' }}
          >
            <input
              type="radio"
              name={props.id}
              value={o.value}
              checked={value.value === o.value}
              onChange={() =>
                props.onChange(
                  o.value === 'ask_owner'
                    ? { value: o.value, confirmWithOwner: false }
                    : { value: o.value, confirmWithOwner: value.confirmWithOwner },
                )
              }
            />
            {o.label}
          </label>
        ))}
      </div>
      {isYesNo && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-2)',
            fontWeight: 'normal',
          }}
        >
          <input
            type="checkbox"
            checked={value.confirmWithOwner}
            onChange={(e) => props.onChange({ ...value, confirmWithOwner: e.target.checked })}
          />
          Confirm with the owner
        </label>
      )}
      {props.hint && <span className="helper">{props.hint}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The unified page
// ---------------------------------------------------------------------------

export function PropertyManagementAgreementPage(): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [notEnabled, setNotEnabled] = useState(false);
  const [result, setResult] = useState<InitiateOwnerIntakeResult | null>(null);

  // --- Modify mode (?from=<groupId>[&mode=<...>]): pre-load from an existing engagement ---
  const [searchParams, setSearchParams] = useSearchParams();
  const modifyGroupId = searchParams.get('from');
  // The out-for-signature Modify mode (validated to the enum; anything else → null).
  const rawMode = searchParams.get('mode');
  const modifyMode: OwnerIntakeSendMode | null =
    rawMode === 'update' || rawMode === 'restart-signing' || rawMode === 'restart-intake'
      ? rawMode
      : null;
  const [modifyDetail, setModifyDetail] = useState<OwnerIntakeDetail | null>(null);
  const [modifyLoading, setModifyLoading] = useState(!!modifyGroupId);
  const [modifyError, setModifyError] = useState<string | null>(null);
  /** Redo-vs-use-existing (only asked when the intake already gathered answers). */
  const [intakeMode, setIntakeMode] = useState<'use_existing' | 'redo' | null>(null);
  /** Result of auto-closing the previous intake after a modify-send (null = n/a). */
  const [prevIntakeClosed, setPrevIntakeClosed] = useState<boolean | null>(null);
  /** Which path the last send took — drives the confirmation copy. */
  const [sentVia, setSentVia] = useState<'intake' | 'regenerate' | null>(null);

  // Owners
  const [owners, setOwners] = useState<OwnerRow[]>([blankOwner()]);
  /** §3/§13 override: collect owner details even though every owner is existing. */
  const [collectIntakeAnyway, setCollectIntakeAnyway] = useState(false);
  /**
   * §13.1: is owner matching on? Until a probe (or any match) says so the form is
   * exactly today's — no Existing / New choice, no similar-name checks.
   */
  const [matchAvailable, setMatchAvailable] = useState(false);
  useEffect(() => {
    let live = true;
    void probeOwnerMatch().then((on) => {
      if (live && on) setMatchAvailable(true);
    });
    return () => {
      live = false;
    };
  }, []);

  // Property (address only — a NEW property not yet in AppFolio).
  const [propertyAddress, setPropertyAddress] = useState('');
  const [propertyType, setPropertyType] = useState<EpmaPropertyType>('Single Family Residence');
  const [numberOfUnits, setNumberOfUnits] = useState('');

  // Term
  const [startDate, setStartDate] = useState('');

  // Compensation
  const [managementPercent, setManagementPercent] = useState('8');
  const [placementPercent, setPlacementPercent] = useState('50');

  // §21 "items WHP manages" (default staff-yes; WHP typically manages these).
  const [propertyTaxes, setPropertyTaxes] = useState<OwnerIntakeFieldControl>(blankControl('yes'));
  const [insurancePremiums, setInsurancePremiums] = useState<OwnerIntakeFieldControl>(blankControl('yes'));
  // Mortgage — a §21 "does WHP pay it" item too (WHP SOMETIMES pays the owner's
  // mortgage). Default ask_owner (staff usually don't know up front).
  const [mortgage, setMortgage] = useState<OwnerIntakeFieldControl>(blankControl('ask_owner'));

  // Rents
  const [targetRents, setTargetRents] = useState('');
  const [minimumRents, setMinimumRents] = useState('');

  // Utilities (default Included)
  const [utilities, setUtilities] = useState<UtilityState>({
    water: 'included',
    sewer: 'included',
    garbage: 'included',
    electricity: 'included',
    gas: 'included',
    yardCare: 'included',
  });

  // Irrigation / winterization
  const [hasIrrigation, setHasIrrigation] = useState<OwnerIntakeFieldControl>(blankControl('ask_owner'));
  const [needsWinterizing, setNeedsWinterizing] = useState<OwnerIntakeFieldControl>(blankControl('ask_owner'));
  const [winterizationInstructions, setWinterizationInstructions] = useState('');

  // HOA
  const [inHoa, setInHoa] = useState<OwnerIntakeFieldControl>(blankControl('ask_owner'));
  const [payHoaDues, setPayHoaDues] = useState<OwnerIntakeFieldControl>(blankControl('ask_owner'));

  // Additional terms
  const [otherItems, setOtherItems] = useState('');

  const updateOwner = (key: number, patch: Partial<OwnerRow>) => {
    setOwners((prev) => prev.map((o) => (o.key === key ? { ...o, ...patch } : o)));
  };

  // -------------------------------------------------------------------------
  // §3 existing-owner lookup. Runs for the given rows (an email blur, or the
  // rows a Modify seeded); the answer lands only if that row still carries the
  // same email and is still unlinked, so a late reply never overwrites a newer
  // edit. Any failure reads as "no matches" (lookupOwnersByEmail) — the form is
  // then exactly what it was before this feature.
  // -------------------------------------------------------------------------
  const lookupRows = (rows: OwnerRow[]) => {
    for (const row of rows) {
      const email = normalizeEmail(row.email);
      if (!isEmailValid(email) || row.appfolioOwnerId || row.lookup?.email === email) continue;
      void lookupOwnersByEmail(email).then((matches) => {
        if (matches.length > 0) setMatchAvailable(true);
        setOwners((prev) =>
          prev.map((o) =>
            o.key === row.key && !o.appfolioOwnerId && normalizeEmail(o.email) === email
              ? { ...o, lookup: { email, matches } }
              : o,
          ),
        );
      });
    }
  };

  /**
   * Link a row to an AppFolio owner (the email banner, the Existing picker, or the
   * similar-name warning) — the row becomes an EXISTING owner and takes the name
   * from AppFolio. The picker / similar paths also take AppFolio's email; the
   * banner keeps the typed one (it is what matched). Everything stays editable.
   */
  const pickOwnerMatch = (key: number, m: OwnerMatch, opts: { fillEmail?: boolean } = {}) => {
    setOwners((prev) =>
      prev.map((o) =>
        o.key === key
          ? {
              ...o,
              ...applyOwnerMatch(o, m),
              ownerKindBeforeLink: o.ownerKind,
              ownerKind: 'existing' as EpmaOwnerKind,
              ...(opts.fillEmail && m.primaryEmail?.trim() ? { email: m.primaryEmail.trim() } : {}),
            }
          : o,
      ),
    );
  };

  /** §13.1: the similar-name check for a NEW owner, once its name is typed. */
  const checkSimilar = (row: OwnerRow) => {
    if (!matchAvailable || row.ownerKind !== 'new' || row.appfolioOwnerId) return;
    const key = similarNameKey(row);
    const q = similarQueryFor(row);
    if (!key || !q || row.similar?.key === key) return;
    void findSimilarOwners(q).then((candidates) => {
      setOwners((prev) =>
        prev.map((o) =>
          o.key === row.key && similarNameKey(o) === key
            ? { ...o, similar: { key, candidates } }
            : o,
        ),
      );
    });
  };

  const dismissSimilar = (key: number) => {
    setOwners((prev) =>
      prev.map((o) => (o.key === key ? { ...o, similarDismissedKey: similarNameKey(o) } : o)),
    );
  };

  /** §13.1: Existing ↔ New. Going New drops any link (the typed fields stay). */
  const setOwnerKind = (row: OwnerRow, kind: EpmaOwnerKind) => {
    if (row.ownerKind === kind) return;
    const next: OwnerRow =
      kind === 'new'
        ? { ...row, ownerKind: 'new', appfolioOwnerId: undefined, linkedMatch: undefined }
        : { ...row, ownerKind: 'existing' };
    setOwners((prev) => prev.map((o) => (o.key === row.key ? next : o)));
    if (kind === 'new') checkSimilar(next);
  };
  const dismissOwnerMatch = (key: number) => {
    setOwners((prev) =>
      prev.map((o) =>
        o.key === key ? { ...o, dismissedEmail: o.lookup?.email ?? normalizeEmail(o.email) } : o,
      ),
    );
  };
  // "Not this owner": drop the link, go back to the kind the row had before it
  // (a link made from a New owner's warning returns to New), and don't re-offer
  // the same email / name suggestion.
  const unlinkOwnerMatch = (key: number) => {
    setOwners((prev) =>
      prev.map((o) =>
        o.key === key
          ? {
              ...o,
              appfolioOwnerId: undefined,
              linkedMatch: undefined,
              ownerKind: o.ownerKindBeforeLink ?? o.ownerKind,
              ownerKindBeforeLink: undefined,
              dismissedEmail: normalizeEmail(o.email),
              similarDismissedKey: similarNameKey(o),
            }
          : o,
      ),
    );
  };

  // Parent onChange wrappers: when a parent control leaves 'yes', its now-hidden
  // dependents must not send a stale value — reset them to the neutral default
  // (ask_owner), consistent with how the server strips inapplicable answers.
  const onIrrigationChange = (v: OwnerIntakeFieldControl) => {
    setHasIrrigation(v);
    if (v.value !== 'yes') {
      setNeedsWinterizing(blankControl('ask_owner'));
      setWinterizationInstructions('');
    }
  };
  const onNeedsWinterizingChange = (v: OwnerIntakeFieldControl) => {
    setNeedsWinterizing(v);
    if (v.value !== 'yes') setWinterizationInstructions('');
  };
  const onInHoaChange = (v: OwnerIntakeFieldControl) => {
    setInHoa(v);
    if (v.value !== 'yes') setPayHoaDues(blankControl('ask_owner'));
  };

  // -------------------------------------------------------------------------
  // Modify mode: seed the builder from an existing engagement's detail.
  //
  // `useExisting` overlays the OWNER'S submitted answers on top of the stored
  // staff controls as FINAL yes/no values (confirmWithOwner off), so the fresh
  // intake only re-asks what staff flip back to "Ask the owner" (plus the §25
  // notice block, which is always owner-entered). `redo` seeds the original
  // staff controls verbatim — the owners answer everything again.
  //
  // KNOWN PRELOAD LIMIT: the detail returns each owner's DISPLAY name only.
  // Individuals are split back into first/last on the last space; entity signer
  // first/last name + title aren't stored on the detail and must be re-entered
  // (the banner says so, and the existing owner validation enforces it).
  // -------------------------------------------------------------------------
  const applySeed = (d: OwnerIntakeDetail, useExisting: boolean) => {
    const a: OwnerIntakeAnswers = useExisting ? d.answers : {};
    const seededOwners: OwnerRow[] = d.owners.map((o) => {
      if (o.ownerType === 'entity') {
        return {
          key: nextKey++,
          ownerType: 'entity' as EpmaOwnerType,
          firstName: '',
          lastName: '',
          entityName: o.name,
          signerTitle: '',
          email: o.email,
          ownerKind: 'new' as EpmaOwnerKind,
        };
      }
      const parts = o.name.trim().split(/\s+/).filter(Boolean);
      const last = parts.length > 1 ? parts[parts.length - 1]! : '';
      const first = parts.length > 1 ? parts.slice(0, -1).join(' ') : (parts[0] ?? '');
      return {
        key: nextKey++,
        ownerType: 'individual' as EpmaOwnerType,
        firstName: first,
        lastName: last,
        entityName: '',
        signerTitle: '',
        email: o.email,
        ownerKind: 'new' as EpmaOwnerKind,
      };
    });
    setOwners(seededOwners);
    // The detail carries no AppFolio link, so re-offer the §3 match for the
    // seeded emails — otherwise a Modify of a linked engagement would quietly
    // send the full new-owner chain.
    lookupRows(seededOwners);
    setPropertyAddress(d.property.address ?? '');
    const sp = d.staffPrefill;
    setPropertyType(sp.propertyType);
    setNumberOfUnits(sp.numberOfUnits != null ? String(sp.numberOfUnits) : '');
    setStartDate(sp.startDate ?? '');
    setManagementPercent(String(sp.managementPercent));
    setPlacementPercent(String(sp.placementPercent));
    setTargetRents(sp.targetRents ?? '');
    setMinimumRents(sp.minimumRents ?? '');
    if (sp.utilities) setUtilities({ ...sp.utilities });
    const ctl = (
      base: OwnerIntakeFieldControl | undefined,
      answered: boolean | undefined,
    ): OwnerIntakeFieldControl =>
      answered !== undefined
        ? { value: answered ? 'yes' : 'no', confirmWithOwner: false }
        : base
          ? { ...base }
          : blankControl('ask_owner');
    setPropertyTaxes(ctl(sp.propertyTaxes, a.whpPaysPropertyTaxes));
    setInsurancePremiums(ctl(sp.insurancePremiums, a.whpPaysInsurance));
    setMortgage(ctl(sp.mortgage, a.whpPaysMortgage));
    // Dependent gates mirror the onChange wrappers: an answered/staff "no" parent
    // resets its now-irrelevant dependents to the neutral default.
    const hi = ctl(sp.hasIrrigation, a.hasIrrigation);
    setHasIrrigation(hi);
    let nw = ctl(sp.needsWinterizing, a.needsWinterizing);
    let wi =
      (useExisting ? a.winterizationInstructions : undefined) ??
      sp.winterizationInstructions ??
      '';
    if (hi.value === 'no') {
      nw = blankControl('ask_owner');
      wi = '';
    }
    setNeedsWinterizing(nw);
    setWinterizationInstructions(wi);
    const ih = ctl(sp.inHoa, a.inHoa);
    setInHoa(ih);
    setPayHoaDues(
      ih.value === 'no'
        ? blankControl('ask_owner')
        : ctl(sp.payHoaDues, a.wantsWhpPayHoaDues),
    );
  };

  useEffect(() => {
    if (!modifyGroupId) return;
    let live = true;
    setModifyLoading(true);
    setModifyError(null);
    void (async () => {
      try {
        const d = await getOwnerIntakeDetail(modifyGroupId);
        if (!live) return;
        setModifyDetail(d);
        // Out-for-signature Modify (&mode set): the redo-vs-use-existing CHOICE is
        // bypassed — the mode already decided it. update / restart-signing carry the
        // existing answers in as final; restart-intake re-asks everything.
        if (modifyMode) {
          const useExisting = modifyMode === 'update' || modifyMode === 'restart-signing';
          applySeed(d, useExisting);
          setIntakeMode(useExisting ? 'use_existing' : 'redo');
        } else if (!d.intakeGathered) {
          // Intake-phase Modify with no answers yet → nothing to choose; seed straight.
          applySeed(d, false);
          setIntakeMode('redo');
        }
      } catch (err) {
        if (!live) return;
        setModifyError(
          err instanceof OwnerIntakeNotEnabledError
            ? err.message
            : err instanceof ApiError && err.status === 404
              ? "That owner intake couldn't be found — you're starting a fresh agreement instead."
              : err instanceof ApiError
                ? ((err.body as { error?: string } | null)?.error ??
                  `Couldn't load the engagement (HTTP ${err.status}) — starting fresh.`)
                : "Couldn't load the engagement — starting fresh.",
        );
      } finally {
        if (live) setModifyLoading(false);
      }
    })();
    return () => {
      live = false;
    };
    // applySeed only touches state setters (stable for the component lifetime).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifyGroupId]);

  const mgmtNum = Number(managementPercent);
  const placeNum = Number(placementPercent);
  const unitsNum = Number(numberOfUnits);

  const endDate = useMemo(() => computeEndDate(startDate), [startDate]);

  // Conditional visibility — mirror the server's computeApplicableQuestions gating
  // on the staff side. A dependent field is shown ONLY when its parent control is a
  // final staff "yes". If the parent is 'no' the dependent is irrelevant; if it's
  // 'ask_owner' (or a confirm) the dependent is deferred to the OWNER's intake too,
  // so staff don't answer it here. (Server: pushControlQuestion runs only inside the
  // resolveHasIrrigation / resolveInHoa gates.)
  const showWinterizing = hasIrrigation.value === 'yes';
  // Winterization instructions only make sense when the irrigation is winterized —
  // i.e. both the irrigation gate AND the winterizing gate are a staff "yes".
  const showWinterizationInstructions = showWinterizing && needsWinterizing.value === 'yes';
  const showPayHoaDues = inHoa.value === 'yes';

  // ---- Regenerate-vs-intake decision (out-for-signature Modify) ------------
  // The GENUINE DIFF: which owner-facing fields would require NEW owner input —
  // "Ask the owner"/confirm in the current config AND not already answered on the
  // record. Fields flipped Ask→Yes/No, or already answered, need nothing new.
  const isSignModifyMode = modifyMode === 'update' || modifyMode === 'restart-signing';
  const existingAnswers = modifyDetail?.answers ?? {};
  const newOwnerFieldKeys = epmaNewOwnerFields(
    {
      propertyTaxes,
      insurancePremiums,
      mortgage,
      hasIrrigation,
      needsWinterizing,
      inHoa,
      payHoaDues,
    },
    existingAnswers,
  );
  // Regenerate-and-sign when we're in a sign-modify mode and the change needs NO new
  // owner input; otherwise (new input needed, or restart-intake) send a minimal intake.
  const willRegenerate = isSignModifyMode && newOwnerFieldKeys.length === 0;
  const askingAboutLabel = newOwnerFieldKeys
    .map((k) => OWNER_FIELD_LABELS[k] ?? k)
    .join(', ');

  // §3/§13: the match prompt, the Existing / New choice, the linked chip and the
  // override only mean something on the INTAKE path — a regenerate
  // reuses the engagement whose chain plan was already decided — so they are
  // hidden while this Modify would regenerate.
  const offerOwnerMatch = !willRegenerate;
  // §13.1: the override exists only when EVERY owner is an existing, linked owner.
  const allExisting = offerOwnerMatch && allOwnersExisting(owners);

  // Each owner needs a name (+ entity name for entities) AND a valid email. The
  // server REQUIRES every owner to have an email at initiate (the agreement
  // auto-sends on completion and can't email a no-email owner), so we enforce it
  // here too — a partial-email submit would just 400.
  const ownerRowValid = (o: OwnerRow): boolean => {
    const hasName = o.firstName.trim().length > 0 && o.lastName.trim().length > 0;
    if (!hasName) return false;
    if (o.ownerType === 'entity' && !o.entityName.trim()) return false;
    if (!isEmailValid(o.email)) return false;
    // §13.1: "Existing owner" means one picked from AppFolio — not a typed guess.
    if (matchAvailable && offerOwnerMatch && o.ownerKind === 'existing' && !o.appfolioOwnerId) return false;
    return true;
  };

  const validOwners = owners.filter(ownerRowValid);
  const propertyValid = propertyAddress.trim().length > 0;
  // Start date is REQUIRED: the send path validates it and would otherwise wedge the
  // intake in a 502 at owner-completion (the owner can't supply a staff-only field).
  const startValid = /^\d{4}-\d{2}-\d{2}$/.test(startDate);
  const compValid =
    Number.isFinite(mgmtNum) && mgmtNum >= 0 && Number.isFinite(placeNum) && placeNum >= 0;
  const unitsValid =
    propertyType !== 'Multi Family Residence' ||
    numberOfUnits.trim() === '' ||
    (Number.isInteger(unitsNum) && unitsNum >= 1);

  const canSend =
    !busy &&
    validOwners.length === owners.length &&
    owners.length >= 1 &&
    propertyValid &&
    startValid &&
    compValid &&
    unitsValid;


  const handleSend = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      const property = { address: propertyAddress.trim() };
      const ownersPayload = owners.map((o) => ({
        ownerType: o.ownerType,
        firstName: o.firstName.trim(),
        lastName: o.lastName.trim(),
        ...(o.ownerType === 'entity'
          ? { entityName: o.entityName.trim(), signerTitle: o.signerTitle.trim() }
          : {}),
        ...(o.email.trim() ? { email: o.email.trim() } : {}),
      }));
      const staffPrefill = {
        propertyTaxes,
        insurancePremiums,
        mortgage,
        hasIrrigation,
        needsWinterizing,
        ...(winterizationInstructions.trim()
          ? { winterizationInstructions: winterizationInstructions.trim() }
          : {}),
        inHoa,
        payHoaDues,
        managementPercent: mgmtNum,
        placementPercent: placeNum,
        ...(targetRents.trim() ? { targetRents: targetRents.trim() } : {}),
        ...(minimumRents.trim() ? { minimumRents: minimumRents.trim() } : {}),
        utilities,
        propertyType,
        ...(propertyType === 'Multi Family Residence' && numberOfUnits.trim()
          ? { numberOfUnits: unitsNum }
          : {}),
        ...(startDate.trim() ? { startDate } : {}),
      };

      // REGENERATE-AND-SIGN: a staff-only change (no new owner input needed) on an
      // out-for-signature agreement. Reuse the SAME engagement — the live submission
      // was already withdrawn by the Modify action — and re-send for signature.
      if (willRegenerate && modifyGroupId) {
        const res = await regenerateOwnerAgreement(modifyGroupId, {
          property,
          owners: ownersPayload,
          staffPrefill,
        });
        setResult({
          groupId: res.groupId,
          status: res.status,
          ownersEmailed: res.ownersEmailed,
          owners: res.owners,
        });
        setSentVia('regenerate');
        const n = res.ownersEmailed;
        toast.success(
          `Sent the updated agreement to ${n} ${n === 1 ? 'owner' : 'owners'} to sign.`,
          { title: 'Property management agreement' },
        );
        return;
      }

      // INTAKE PATH: a fresh send, the intake-phase Modify, or an out-for-signature
      // Modify that genuinely needs new owner input (a MINIMAL intake asks only the
      // new field(s)). Threads `mode` (owner-email copy) and, for an out-for-signature
      // Modify, `supersedeGroupId` — the server silently withdraws the OLD live
      // submission AFTER this new intake is out (atomic-with-send, never on click).
      // §3: a linked owner carries its appfolioOwnerId, and the override rides along
      // only when something is linked — with no link the body is exactly today's.
      const res = await initiateOwnerIntake({
        property,
        owners: ownersPayload.map((p, i) => {
          const linkedId = owners[i]?.appfolioOwnerId;
          return linkedId ? { ...p, appfolioOwnerId: linkedId } : p;
        }),
        staffPrefill,
        ...(modifyMode ? { mode: modifyMode } : {}),
        ...(modifyMode && modifyGroupId ? { supersedeGroupId: modifyGroupId } : {}),
        ...(allExisting ? { collectIntakeAnyway } : {}),
      });
      // Intake-PHASE Modify (no mode → the previous engagement is an OPEN intake, no
      // live submission): soft-cancel that open intake so the owners never hold two
      // live intake links. The out-for-signature Modify (mode set) supersedes the old
      // SUBMISSION server-side instead — nothing to soft-cancel here.
      if (modifyGroupId && !modifyMode) {
        try {
          await cancelOwnerIntake(modifyGroupId, 'Superseded by a revised intake');
          setPrevIntakeClosed(true);
        } catch (err) {
          if (err instanceof ApiError && err.status === 409) {
            // Already completed/closed on its own — nothing left to close. If it
            // completed, an agreement may exist; that's managed from its thread.
            setPrevIntakeClosed(null);
            toast.info(
              'The previous intake had already completed or was closed — nothing to close. If it generated an agreement, manage that from its thread.',
            );
          } else {
            setPrevIntakeClosed(false);
            toast.warning(
              "Couldn't close the previous intake — cancel it from its thread so the owners don't have two live links.",
            );
          }
        }
      }
      setResult(res);
      setSentVia('intake');
      const n = res.ownersEmailed;
      toast.success(
        `Sent the ${modifyMode ? 'request ' : 'agreement '}to ${n} ${n === 1 ? 'owner' : 'owners'}${modifyMode ? '' : ' to fill out'}.`,
        { title: 'Property management agreement' },
      );
    } catch (err) {
      if (err instanceof OwnerIntakeNotEnabledError) {
        setNotEnabled(true);
      } else {
        const msg =
          err instanceof ApiError
            ? (ownerMatchInitiateError(err.body) ??
              (err.body as { error?: string } | null)?.error ??
              `Could not send the agreement (HTTP ${err.status})`)
            : err instanceof Error
              ? err.message
              : 'Could not send the property management agreement';
        toast.error(msg, { title: 'Property management agreement' });
      }
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setResult(null);
    setOwners([blankOwner()]);
    setCollectIntakeAnyway(false);
    setPropertyAddress('');
    // Leave modify mode entirely — "start another" means a FRESH agreement.
    if (modifyGroupId) setSearchParams({}, { replace: true });
    setModifyDetail(null);
    setModifyError(null);
    setIntakeMode(null);
    setPrevIntakeClosed(null);
    setSentVia(null);
  };

  // --- Post-send confirmation ---
  if (result) {
    // §3: "Owner contact step skipped — existing owner" etc. Null when nothing was
    // skipped or the server sent no plan (flag off) → today's confirmation.
    const sentChainSummary = chainPlanSummary(result.chainPlan, result.owners);
    return (
      <div style={{ maxWidth: 760 }}>
        <div className="alert" data-tone="success" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">
            {sentVia === 'regenerate' ? (
              <>
                <div className="alert-title">Updated agreement sent to sign</div>
                We emailed the updated agreement to {result.ownersEmailed}{' '}
                {result.ownersEmailed === 1 ? 'owner' : 'owners'} for{' '}
                <strong>{propertyAddress.trim()}</strong> to review and sign. The previous
                version was withdrawn — the owners&apos; old links no longer work. Track it
                on the Requests page.
              </>
            ) : (
              <>
                <div className="alert-title">Sent to the owners</div>
                We emailed the agreement to {result.ownersEmailed}{' '}
                {result.ownersEmailed === 1 ? 'owner' : 'owners'} for{' '}
                <strong>{propertyAddress.trim()}</strong>. Each owner confirms the details we
                deferred and enters their legal-notice address; the first owner to finish locks
                in the answers, and the agreement is generated and sent for signature
                automatically. Track it on the Requests page.
                {prevIntakeClosed === true && (
                  <>
                    {' '}
                    The previous intake was closed — the owners&apos; old links no longer
                    work.
                  </>
                )}
                {sentChainSummary && (
                  <div style={{ marginTop: 'var(--space-2)' }} data-testid="chain-plan-summary">
                    {sentChainSummary}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {prevIntakeClosed === false && (
          <div className="alert" data-tone="warning" style={{ marginBottom: 'var(--space-5)' }}>
            <div className="alert-body">
              The previous intake could not be closed automatically. If it&apos;s still
              open, cancel it from its thread so the owners don&apos;t have two live
              links.
            </div>
          </div>
        )}

        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Owners on this agreement</h3>
          </div>
          <ul style={{ margin: 0, paddingLeft: 'var(--space-5)' }}>
            {result.owners.map((o) => (
              <li key={o.role} style={{ marginBottom: 'var(--space-2)' }}>
                <strong>{o.name}</strong>{' '}
                <span style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)' }}>
                  ({o.role}) — emailed
                </span>
              </li>
            ))}
          </ul>
        </section>

        <button
          type="button"
          className="btn"
          data-variant="secondary"
          onClick={resetForm}
          style={{ marginTop: 'var(--space-5)' }}
        >
          Start another agreement
        </button>
      </div>
    );
  }

  // --- Modify mode: loading the engagement to pre-fill from ---
  if (modifyGroupId && modifyLoading) {
    return (
      <div style={{ maxWidth: 760 }}>
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
            Property management agreement
          </h1>
        </div>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
          Loading the existing engagement…
        </p>
      </div>
    );
  }

  // --- Modify mode: an owner already answered → redo vs use existing ---
  // Miles's rule: when the intake was already gathered, staff must choose before
  // the builder opens. DEFAULT = use the existing answers (primary button).
  if (modifyDetail && modifyDetail.intakeGathered && intakeMode === null) {
    const answered = Object.keys(modifyDetail.answers).length;
    return (
      <div style={{ maxWidth: 760 }}>
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
            Property management agreement
          </h1>
        </div>
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">The owners already answered this intake</h3>
          </div>
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              For <strong>{modifyDetail.property.address}</strong>, the owners already
              provided {answered > 0 ? `${answered} answer${answered === 1 ? '' : 's'}` : 'answers'}{' '}
              on the intake you&apos;re modifying. You can keep what they told us — their
              answers are carried into the builder as final values, so the fresh intake
              only asks what you change back to &ldquo;Ask the owner&rdquo; (plus their
              legal-notice details) — or redo the intake and ask them everything again.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-2)' }}>
              <button
                type="button"
                className="btn"
                data-variant="primary"
                autoFocus
                onClick={() => {
                  applySeed(modifyDetail, true);
                  setIntakeMode('use_existing');
                }}
              >
                Use their existing answers
              </button>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                onClick={() => {
                  applySeed(modifyDetail, false);
                  setIntakeMode('redo');
                }}
              >
                Redo the intake
              </button>
            </div>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-subtle)', margin: 0 }}>
              Either way, sending emails the owners fresh links and closes the previous
              intake.
            </p>
          </div>
        </section>
      </div>
    );
  }

  const modifying = !!modifyGroupId && modifyDetail !== null;
  const anyEntityOwner = modifyDetail?.owners.some((o) => o.ownerType === 'entity') ?? false;

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Property management agreement
        </h1>
      </div>

      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: '0 0 var(--space-6)' }}>
        Build the Exclusive Property Management Agreement for a new property. Fill in what you already
        know and, for anything you don&apos;t, hand it to the owner — or ask them to confirm your
        answer. Every agreement goes to the owners to complete and sign; the first owner to finish
        locks it in and it&apos;s generated and sent for signature automatically. WHP countersigns
        (Agent, then Broker) after the owners sign. Track it on the Requests page.
      </p>

      {modifyError && (
        <div className="alert" data-tone="warning" role="note" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">{modifyError}</div>
        </div>
      )}

      {modifying && (
        <div className="alert" data-tone="info" role="note" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">
            <div className="alert-title">
              {modifyMode
                ? `Modifying the agreement for ${modifyDetail.property.address}`
                : `Modifying the intake for ${modifyDetail.property.address}`}
            </div>
            {modifyMode ? (
              <>
                The current agreement stays live until you send. When you do, we
                {willRegenerate ? (
                  <> email the owners the updated agreement to sign</>
                ) : (
                  <>
                    {' '}
                    ask the owners for
                    {askingAboutLabel ? ` ${askingAboutLabel}` : ' the new details'} (only
                    what changed — everything they already answered is kept)
                  </>
                )}
                , then withdraw the old version — no separate &ldquo;cancelled&rdquo; email.
              </>
            ) : (
              'Sending emails the owners fresh links and closes the previous intake.'
            )}
            {intakeMode === 'use_existing' && (
              <>
                {' '}
                The owners&apos; previous answers are carried in below as final values —
                flip any back to &ldquo;Ask the owner&rdquo; to re-ask them.
              </>
            )}{' '}
            Owner names were carried over — double-check them
            {anyEntityOwner
              ? '; entity signer names and titles aren’t stored, so re-enter those'
              : ''}
            .
          </div>
        </div>
      )}

      {notEnabled && (
        <div className="alert" data-tone="info" role="note" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">
            <div className="alert-title">Sending isn&apos;t enabled yet</div>
            The Property Management Agreement is a legal document. Once it&apos;s reviewed and the
            feature is turned on (<code>EPMA_SEND_ENABLED</code>), the Send button below goes live.
          </div>
        </div>
      )}

      <div className="stack">
        {/* Owners & parties */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Owners &amp; parties</h3>
          </div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>
            Everyone who signs the agreement. Each owner is independently an individual or an entity
            (LLC / trust) and gets their own emailed form — every owner needs a valid email.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {owners.map((o, idx) => {
              const emailEmpty = !o.email.trim();
              const emailInvalid = !emailEmpty && !isEmailValid(o.email);
              return (
                <div
                  key={o.key}
                  style={{
                    border: '1px solid var(--border, rgba(0,0,0,0.08))',
                    borderRadius: 8,
                    padding: 'var(--space-4)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-3)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 'var(--text-sm)' }}>Owner {idx + 1}</strong>
                    {owners.length > 1 && (
                      <button
                        type="button"
                        className="btn"
                        data-variant="ghost"
                        data-size="sm"
                        data-icon-only
                        aria-label={`Remove owner ${idx + 1}`}
                        onClick={() => setOwners((prev) => prev.filter((x) => x.key !== o.key))}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {matchAvailable && offerOwnerMatch && (
                    <div
                      className="tabs"
                      data-variant="segmented"
                      role="tablist"
                      aria-label={`Owner ${idx + 1}: existing or new`}
                    >
                      {(['existing', 'new'] as const).map((k) => (
                        <button
                          key={k}
                          type="button"
                          className="tab"
                          role="tab"
                          aria-selected={o.ownerKind === k}
                          onClick={() => setOwnerKind(o, k)}
                        >
                          {k === 'existing' ? 'Existing owner' : 'New owner'}
                        </button>
                      ))}
                    </div>
                  )}
                  {matchAvailable && offerOwnerMatch && o.ownerKind === 'existing' && !o.appfolioOwnerId && (
                    <>
                      <OwnerSearchPicker
                        key={`picker-${o.key}`}
                        id={`owner-pick-${o.key}`}
                        label="Find the owner in AppFolio"
                        initialQuery={ownerRowName(o) || o.email.trim()}
                        selected={null}
                        onPick={(m) => pickOwnerMatch(o.key, m, { fillEmail: true })}
                        emptyText="No AppFolio owners match. If they are not in AppFolio yet, choose New owner."
                        showChoiceLine={false}
                      />
                      <span className="helper" data-state="error">
                        Pick the owner from AppFolio, or choose New owner.
                      </span>
                    </>
                  )}
                  <div className="field">
                    <label htmlFor={`owner-type-${o.key}`}>Owner is</label>
                    <select
                      id={`owner-type-${o.key}`}
                      className="input"
                      value={o.ownerType}
                      onChange={(e) => updateOwner(o.key, { ownerType: e.target.value as EpmaOwnerType })}
                    >
                      <option value="individual">An individual</option>
                      <option value="entity">An entity (LLC / trust / company)</option>
                    </select>
                  </div>
                  {o.ownerType === 'entity' && (
                    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                      <div className="field" style={{ flex: 1.4 }}>
                        <label htmlFor={`entity-name-${o.key}`}>Entity name</label>
                        <input
                          id={`entity-name-${o.key}`}
                          type="text"
                          className="input"
                          placeholder="Maple Holdings, LLC"
                          value={o.entityName}
                          onChange={(e) => updateOwner(o.key, { entityName: e.target.value })}
                          onBlur={() => checkSimilar(o)}
                          aria-invalid={!o.entityName.trim()}
                        />
                      </div>
                      <div className="field" style={{ flex: 1 }}>
                        <label htmlFor={`signer-title-${o.key}`}>Signer title / capacity</label>
                        <input
                          id={`signer-title-${o.key}`}
                          type="text"
                          className="input"
                          placeholder="Managing Member"
                          value={o.signerTitle}
                          onChange={(e) => updateOwner(o.key, { signerTitle: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                    <input
                      type="text"
                      className="input"
                      style={{ flex: 1 }}
                      placeholder="Signer first name"
                      value={o.firstName}
                      onChange={(e) => updateOwner(o.key, { firstName: e.target.value })}
                      aria-label={`Owner ${idx + 1} first name`}
                    />
                    <input
                      type="text"
                      className="input"
                      style={{ flex: 1 }}
                      placeholder="Signer last name"
                      value={o.lastName}
                      onChange={(e) => updateOwner(o.key, { lastName: e.target.value })}
                      onBlur={() => checkSimilar(o)}
                      aria-label={`Owner ${idx + 1} last name`}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor={`owner-email-${o.key}`}>Email (where we send their form)</label>
                    <input
                      id={`owner-email-${o.key}`}
                      type="email"
                      className="input"
                      placeholder="owner@example.com"
                      value={o.email}
                      onChange={(e) => updateOwner(o.key, { email: e.target.value })}
                      onBlur={() => lookupRows([o])}
                      aria-invalid={emailEmpty || emailInvalid}
                    />
                    {emailEmpty && <span className="helper" data-state="error">Email required to send.</span>}
                    {emailInvalid && <span className="helper" data-state="error">Enter a valid email.</span>}
                  </div>
                  {offerOwnerMatch && (
                    <OwnerMatchPrompt
                      prompt={ownerMatchPromptFor(o)}
                      linked={o.linkedMatch}
                      onUse={(m) => pickOwnerMatch(o.key, m)}
                      onDismiss={() => dismissOwnerMatch(o.key)}
                      onUnlink={() => unlinkOwnerMatch(o.key)}
                    />
                  )}
                  {matchAvailable && offerOwnerMatch && (
                    <SimilarOwnerWarning
                      candidates={similarCandidatesFor(o)}
                      onUse={(c) => pickOwnerMatch(o.key, c, { fillEmail: true })}
                      onDismiss={() => dismissSimilar(o.key)}
                    />
                  )}
                </div>
              );
            })}
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              onClick={() => setOwners((prev) => [...prev, blankOwner()])}
              style={{ alignSelf: 'flex-start' }}
            >
              + Add owner
            </button>
            {allExisting && (
              <div className="field" style={{ margin: 0 }}>
                <label className="choice" style={{ fontWeight: 'normal' }}>
                  <input
                    type="checkbox"
                    className="check"
                    checked={collectIntakeAnyway}
                    onChange={(e) => setCollectIntakeAnyway(e.target.checked)}
                  />
                  {CONTACT_ONLY_OVERRIDE_LABEL}
                </label>
                <span className="helper">{CONTACT_ONLY_OVERRIDE_HELPER}</span>
              </div>
            )}
          </div>
        </section>

        {/* Property */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Property</h3>
          </div>
          <div className="stack-sm">
            <div className="field">
              <label htmlFor="epma-addr">Property address</label>
              <input
                id="epma-addr"
                type="text"
                className="input"
                placeholder="123 Main St, Walla Walla, WA 99362"
                value={propertyAddress}
                onChange={(e) => setPropertyAddress(e.target.value)}
                aria-invalid={!propertyValid}
              />
              <span className="helper">A new property, not yet in AppFolio — just the address.</span>
            </div>
            <div className="field">
              <label htmlFor="epma-type">Property type</label>
              <select
                id="epma-type"
                className="input"
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value as EpmaPropertyType)}
              >
                <option value="Single Family Residence">Single Family Residence</option>
                <option value="Multi Family Residence">Multi Family Residence</option>
                <option value="Commercial">Commercial</option>
              </select>
            </div>
            {propertyType === 'Multi Family Residence' && (
              <div className="field">
                <label htmlFor="epma-units">Number of units</label>
                <input
                  id="epma-units"
                  type="number"
                  min={1}
                  className="input"
                  placeholder="e.g. 4"
                  value={numberOfUnits}
                  onChange={(e) => setNumberOfUnits(e.target.value)}
                  aria-invalid={!unitsValid}
                />
              </div>
            )}
          </div>
        </section>

        {/* Term */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Term</h3>
          </div>
          <div className="stack-sm">
            <div className="field">
              <label htmlFor="epma-start">Start date</label>
              <input
                id="epma-start"
                type="date"
                className="input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                aria-invalid={!startValid}
              />
              <span className="helper">
                The term is a fixed one (1) year.{' '}
                {endDate
                  ? `End date: ${formatLong(endDate)} (start + 1 year − 1 day).`
                  : 'Enter a start date to see the end date.'}
              </span>
            </div>
          </div>
        </section>

        {/* Compensation */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Compensation</h3>
          </div>
          <div className="stack-sm">
            <div className="field">
              <label htmlFor="epma-mgmt">Management fee (% of gross monthly receipts)</label>
              <input
                id="epma-mgmt"
                type="number"
                min={0}
                className="input"
                value={managementPercent}
                onChange={(e) => setManagementPercent(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="epma-place">Tenant-placement fee (% of one month&apos;s rent)</label>
              <input
                id="epma-place"
                type="number"
                min={0}
                className="input"
                value={placementPercent}
                onChange={(e) => setPlacementPercent(e.target.value)}
              />
              <span className="helper">
                The agreement auto-spells this number into its word form (e.g. 50 → &ldquo;fifty&rdquo;).
              </span>
            </div>
          </div>
        </section>

        {/* Items WHP manages (§21) */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Items WHP manages</h3>
          </div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: '0 0 var(--space-4)' }}>
            Whether WHP pays these on the owner&apos;s behalf. Answer Yes/No if you know, or hand it to
            the owner. (HOA dues are under the HOA section below.)
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <FieldControlField
              id="epma-taxes"
              label="Should WHP pay the property taxes?"
              value={propertyTaxes}
              onChange={setPropertyTaxes}
            />
            <FieldControlField
              id="epma-insurance"
              label="Should WHP pay the insurance premiums?"
              value={insurancePremiums}
              onChange={setInsurancePremiums}
            />
            <FieldControlField
              id="epma-mortgage"
              label="Should WHP pay the mortgage?"
              hint="Yes = WHP manages and pays the mortgage on the owner's behalf (§21). WHP sometimes does."
              value={mortgage}
              onChange={setMortgage}
            />
          </div>
        </section>

        {/* Rents */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Rents</h3>
          </div>
          <div className="stack-sm">
            <div className="field">
              <label htmlFor="epma-target">Target rent(s)</label>
              <input
                id="epma-target"
                type="text"
                className="input"
                placeholder="e.g. $1,800/mo"
                value={targetRents}
                onChange={(e) => setTargetRents(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="epma-min">Minimum rent(s)</label>
              <input
                id="epma-min"
                type="text"
                className="input"
                placeholder="e.g. $1,650/mo"
                value={minimumRents}
                onChange={(e) => setMinimumRents(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Utilities */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Utilities</h3>
          </div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>
            Mark each utility Included (in rent — the owner pays) or Not included (the resident pays).
            Default Included.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {UTILITY_KEYS.map(({ key, label }) => (
              <div key={key} className="field" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <span style={{ flex: 1 }}>{label}</span>
                <div style={{ display: 'flex', gap: 'var(--space-4)', flex: 1 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', margin: 0, fontWeight: 'normal' }}>
                    <input
                      type="radio"
                      name={`util-${key}`}
                      value="included"
                      checked={utilities[key] === 'included'}
                      onChange={() => setUtilities((prev) => ({ ...prev, [key]: 'included' }))}
                    />
                    Included in rent
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', margin: 0, fontWeight: 'normal' }}>
                    <input
                      type="radio"
                      name={`util-${key}`}
                      value="not_included"
                      checked={utilities[key] === 'not_included'}
                      onChange={() => setUtilities((prev) => ({ ...prev, [key]: 'not_included' }))}
                    />
                    Not included in rent
                  </label>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Winterization */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Irrigation &amp; winterization</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <FieldControlField
              id="epma-irrigation"
              label="Does the property have an irrigation system?"
              value={hasIrrigation}
              onChange={onIrrigationChange}
            />
            {showWinterizing && (
              <FieldControlField
                id="epma-winterize"
                label="Does the irrigation need to be winterized each year?"
                value={needsWinterizing}
                onChange={onNeedsWinterizingChange}
              />
            )}
            {showWinterizationInstructions && (
              <div className="field" style={{ margin: 0 }}>
                <label htmlFor="epma-winter">Winterization instructions (optional)</label>
                <textarea
                  id="epma-winter"
                  className="input"
                  rows={3}
                  placeholder="Leave blank to have the owner add them."
                  value={winterizationInstructions}
                  onChange={(e) => setWinterizationInstructions(e.target.value)}
                />
              </div>
            )}
          </div>
        </section>

        {/* HOA */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Homeowners association (HOA)</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <FieldControlField
              id="epma-inhoa"
              label="Is the property in an HOA?"
              hint="When in an HOA, the owner is also asked to upload the CC&Rs."
              value={inHoa}
              onChange={onInHoaChange}
            />
            {showPayHoaDues && (
              <FieldControlField
                id="epma-hoadues"
                label="Should WHP pay the HOA dues on the owner's behalf?"
                value={payHoaDues}
                onChange={setPayHoaDues}
              />
            )}
          </div>
        </section>

        {/* Additional terms */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Additional terms</h3>
          </div>
          <div className="field">
            <label htmlFor="epma-other">Other items (optional)</label>
            <textarea
              id="epma-other"
              className="input"
              rows={3}
              placeholder="Anything else specific to this agreement…"
              value={otherItems}
              onChange={(e) => setOtherItems(e.target.value)}
            />
          </div>
        </section>

        {/* Action */}
        <section className="card">
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              {willRegenerate
                ? 'Regenerates the agreement with these changes and emails the owners the updated version to sign — no new questions, since nothing here needs their input. The previous version is withdrawn once the new one is out.'
                : modifyMode
                  ? 'Emails the owners a short form for just the new detail(s) — everything they already answered is kept. When the first owner finishes, the updated agreement generates and goes out to sign, and the previous version is withdrawn.'
                  : 'Emails each owner (with an email) a form to confirm the deferred details and enter their legal-notice address. The first owner to finish locks the answers and the agreement generates + sends for signature automatically.'}
            </p>
            <button
              type="button"
              className="btn"
              onClick={() => void handleSend()}
              disabled={!canSend}
              style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
            >
              {busy
                ? 'Sending…'
                : willRegenerate
                  ? `Send updated agreement to ${owners.length} ${owners.length === 1 ? 'owner' : 'owners'} for signature →`
                  : modifyMode
                    ? `Request the new details from ${owners.length} ${owners.length === 1 ? 'owner' : 'owners'} →`
                    : modifying
                      ? `Send revised intake to ${owners.length} ${owners.length === 1 ? 'owner' : 'owners'} →`
                      : `Send to ${owners.length} ${owners.length === 1 ? 'owner' : 'owners'} →`}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
